import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";
import { MdClose, MdDelete, MdNotes } from "react-icons/md";
import { endpoints } from "../../../api/endpoints";
import { IReportNote } from "../../../api/types/reports/IReportNote";
import { Color } from "../../../constants/colors";
import { reportTableTheme } from "./reportTableTheme";
import { normalizeReportNote } from "./utils/normalizeReportNote";

const NOTE_MAX_LENGTH = 500;
const PANEL_WIDTH = 320;

interface Props {
  reportId: string;
  open: boolean;
  onToggle: () => void;
}

export function TicketGridReportNotesPanel({ reportId, open, onToggle }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [notes, setNotes] = useState<IReportNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadNotes = useCallback(async () => {
    setLoading(true);

    try {
      const response = await endpoints.report.getReportNotes({ report_id: reportId });

      if (response.error) {
        enqueueSnackbar(response.error, { variant: "error" });
        setNotes([]);
        return;
      }

      setNotes((response.notes ?? []).map((note) => normalizeReportNote(note)));
    } catch {
      enqueueSnackbar("Не удалось загрузить примечания", { variant: "error" });
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, reportId]);

  useEffect(() => {
    if (open) {
      void loadNotes();
    }
  }, [loadNotes, open]);

  const handleCreateNote = useCallback(async () => {
    const text = newNoteText.trim();
    if (!text || creating) {
      return;
    }

    setCreating(true);

    try {
      const response = await endpoints.report.createReportNote({
        report_id: reportId,
        text,
      });

      if (response.error) {
        enqueueSnackbar(response.error, { variant: "error" });
        return;
      }

      setNewNoteText("");
      await loadNotes();
      enqueueSnackbar("Примечание добавлено", { variant: "success" });
    } catch {
      enqueueSnackbar("Не удалось добавить примечание", { variant: "error" });
    } finally {
      setCreating(false);
    }
  }, [creating, enqueueSnackbar, loadNotes, newNoteText, reportId]);

  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      if (deletingId) {
        return;
      }

      setDeletingId(noteId);

      try {
        const response = await endpoints.report.deleteReportNote({ id: noteId });

        if (response.error) {
          enqueueSnackbar(response.error, { variant: "error" });
          return;
        }

        setNotes((current) => current.filter((note) => note.id !== noteId));
        enqueueSnackbar("Примечание удалено", { variant: "success" });
      } catch {
        enqueueSnackbar("Не удалось удалить примечание", { variant: "error" });
      } finally {
        setDeletingId(null);
      }
    },
    [deletingId, enqueueSnackbar],
  );

  const canCreate = newNoteText.trim().length > 0 && !creating;

  if (!open) {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 20,
          zIndex: 10,
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<MdNotes size={18} />}
          onClick={onToggle}
          sx={{
            backgroundColor: Color.brand,
            color: Color.text.onDark,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13px",
            px: 1.75,
            py: 0.75,
            boxShadow: "0 2px 8px rgba(44, 104, 98, 0.35)",
            "&:hover": {
              backgroundColor: Color.brand,
              filter: "brightness(0.9)",
              boxShadow: "0 2px 8px rgba(44, 104, 98, 0.35)",
            },
          }}
        >
          Примечания
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: PANEL_WIDTH,
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        borderLeft: reportTableTheme.border,
        backgroundColor: "#fafafa",
        boxShadow: "-4px 0 16px rgba(0, 0, 0, 0.12)",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: reportTableTheme.border,
          backgroundColor: reportTableTheme.headerBg,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "12px",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: reportTableTheme.headerText,
          }}
        >
          Примечания
        </Typography>
        <Tooltip title="Скрыть">
          <IconButton size="small" onClick={onToggle}>
            <MdClose size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          px: 2,
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            Примечаний пока нет
          </Typography>
        ) : (
          notes.map((note) => (
            <Box
              key={note.id}
              sx={{
                border: reportTableTheme.border,
                borderRadius: 1,
                backgroundColor: "#ffffff",
                p: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 0.75,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: "11px" }}
                >
                  {note.date}
                </Typography>
                <IconButton
                  size="small"
                  disabled={deletingId === note.id}
                  onClick={() => void handleDeleteNote(note.id)}
                  sx={{ mt: -0.5, mr: -0.5 }}
                  title="Удалить"
                >
                  <MdDelete size={16} />
                </IconButton>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "13px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {note.text}
              </Typography>
            </Box>
          ))
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          borderTop: reportTableTheme.border,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          backgroundColor: "#ffffff",
        }}
      >
        <TextField
          multiline
          minRows={3}
          maxRows={6}
          size="small"
          placeholder="Новое примечание"
          value={newNoteText}
          inputProps={{ maxLength: NOTE_MAX_LENGTH }}
          onChange={(event) => setNewNoteText(event.target.value)}
          fullWidth
        />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="caption" color="text.secondary">
            {newNoteText.length}/{NOTE_MAX_LENGTH}
          </Typography>
          <Button
            variant="contained"
            size="small"
            disabled={!canCreate}
            onClick={() => void handleCreateNote()}
            sx={{
              backgroundColor: Color.brand,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: Color.brand,
                filter: "brightness(0.9)",
                boxShadow: "none",
              },
            }}
          >
            {creating ? "Добавление…" : "Добавить"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
